/**
 * GAPI-30848 (Bug B last mile) — dispatch `setMultiTechAreaGroupsAction`
 * with an initial `selectedTechByArea` derived from the loaded design's
 * spread names.
 *
 * Without this, opening a published mixed-tech product in Edit-designs
 * defaults every area's tech to `options[0].technology` (whatever the
 * catalogue ordered first — usually DTF). The customer's saved per-area
 * pick (e.g. Back→DTG) is silently flipped back to DTF on every reopen.
 *
 * The host bridge already canonicalises the saved `designStructureJson`
 * spread names at publish time so they encode the per-area tech the
 * customer chose (`back-dtg`, `front-dtf`, …). Here on load we walk those
 * spread names, match them to each areaGroup's option whose `spreadName`
 * equals (case-insensitively) the spread, and seed `selectedTechByArea`
 * with the matching `option.technology`. Areas without a matching spread
 * fall back to `options[0].technology` — same as the legacy default.
 */
import { setMultiTechAreaGroupsAction } from 'editor/src/store/app/slice';
import { MultiTechAreaGroup } from 'editor/src/store/app/types';
import type { ThunkDispatch } from 'editor/src/store/hooks';
import { RootState } from 'editor/src/store/index';

// GAPI-30848: Keep in sync with multi-tech-iframe-bridge.service.ts on the
// storefront side. Placeholder spreads are clones of the first saved spread
// minted to give the strip a tile for every (area × tech) combo even when the
// customer hasn't designed that tech yet. Those clones look identical to real
// saved content, so without this tag we'd pick the wrong tech on reopen when
// both `back-dtg` (saved) and `back-dtf` (placeholder) end up in the design.
const MULTI_TECH_PLACEHOLDER_TAG = '__multiTechPlaceholder';

// GAPI-30848 — Count of items in a spread's `media` + `content` groups across
// all pages. The catalogue template ships every spread with a base count
// (typically layout markers); customer-placed shapes/text/images add to this
// total. Comparing same-area DTF vs DTG mediaCount lets us detect which side
// the customer's artwork lives on — the side with the HIGHER count has the
// extra customer content. Cloned bridge placeholders match the source's count
// exactly, so they only win ties, never beat a real-content spread.
const spreadContentCount = (spread: {
  pages?: Array<{ groups?: { media?: unknown[]; content?: unknown[] } }>;
}): number =>
  (spread?.pages ?? []).reduce(
    (sum, p) => sum + (p?.groups?.media?.length ?? 0) + (p?.groups?.content?.length ?? 0),
    0,
  );

type SpreadLike = {
  name?: string;
  tags?: string[];
  pages?: Array<{ groups?: { media?: unknown[]; content?: unknown[] } }>;
};

const initMultiTechAreaGroupsOperation =
  (areaGroups: MultiTechAreaGroup[]) => (dispatch: ThunkDispatch, getState: () => RootState) => {
    const state = getState();
    // Index ALL spreads (placeholder-tagged or not) by lowercased name.
    // Placeholder tag is used as a TIEBREAKER below, not as a filter — the
    // bridge sometimes tags spreads whose content has since been reflected
    // onto, so excluding them outright hides real customer artwork.
    const spreadByName = new Map<string, SpreadLike>();
    (state.design.designData?.spreads ?? []).forEach((s) => {
      if (!s?.name) {
        return;
      }
      spreadByName.set(s.name.toLowerCase(), s);
    });

    // GAPI-30848 (Bug 3) — In the wizard's Edit-designs flow the bridge fires
    // this op twice in quick succession: once for the saved variant and once
    // for the catalogue's default `designTemplates`. Preserve any per-area
    // value the customer's saved design already pinned. Tech-switch
    // (Embroidery ↔ Printing) clears `selectedTechByArea` by sending an empty
    // `areaGroups` first, so this preservation doesn't strand stale picks.
    const existingByArea = (state.app.selectedTechByArea ?? {}) as Record<string, string>;

    const selectedTechByArea: Record<string, string> = {};
    areaGroups.forEach((group) => {
      // GAPI-30848 — For each canonical area, score every option's spread by
      // (contentCount, !isPlaceholder). Highest content wins; ties go to the
      // non-placeholder spread (real saved beats a clone with identical
      // content). This handles every observed case:
      //   - Customer published Front-DTG → front-dtg has +1 content over
      //     front-dtf's template baseline → DTG wins on contentCount.
      //   - Customer published Back-DTG (saved) + bridge cloned Back-DTF
      //     placeholder with identical content → tie on contentCount, but
      //     back-dtg is non-placeholder → DTG wins on tiebreaker.
      //   - Customer left area at the catalogue default (no artwork either
      //     side) → mediaCount equal → tiebreaker picks the non-placeholder
      //     (the saved variant's tech), fallback options[0] if none.
      type Scored = {
        technology: string;
        spread: SpreadLike | undefined;
        contentCount: number;
        isPlaceholder: boolean;
      };
      const scored: Scored[] = group.options.map((opt) => {
        const spread = opt.spreadName ? spreadByName.get(opt.spreadName.toLowerCase()) : undefined;
        return {
          technology: opt.technology,
          spread,
          contentCount: spread ? spreadContentCount(spread) : -1,
          isPlaceholder: Boolean(spread?.tags?.includes(MULTI_TECH_PLACEHOLDER_TAG)),
        };
      });
      const withSpread = scored.filter((s) => s.spread);
      if (withSpread.length) {
        const maxContent = Math.max(...withSpread.map((s) => s.contentCount));
        const topByContent = withSpread.filter((s) => s.contentCount === maxContent);
        // GPS-25623 (2026-05-27) — when the customer manually picked a tech
        // via the bottom-strip popover (existingByArea has a value for this
        // area), prefer that pick over the scoring tiebreakers if the
        // top-by-content tier still includes it. Without this, the bridge's
        // follow-up `setMultiTechAreaGroups` re-fire after the popover
        // commit re-derives selectedTechByArea from scratch — both
        // tech spreads are placeholder clones with identical content, so
        // the manual DTG pick gets overwritten by options[0] (catalogue
        // default, typically DTF) and the badge visually reverts. Customer
        // sees "no switch happened". Only applies when the pick is still
        // a valid top-tier candidate, so artwork-driven scoring on
        // Edit-Designs reopen (the original purpose of this scoring)
        // continues to win when the customer has real content on one tech.
        const previousPick = existingByArea[group.canonicalName];
        const userPicked = previousPick && topByContent.find((s) => s.technology === previousPick);
        if (userPicked) {
          selectedTechByArea[group.canonicalName] = userPicked.technology;
          return;
        }
        const nonPlaceholderTop = topByContent.find((s) => !s.isPlaceholder);
        const winner = nonPlaceholderTop ?? topByContent[0];
        selectedTechByArea[group.canonicalName] = winner.technology;
        return;
      }
      // No spread evidence for this area — preserve a previously-pinned
      // selection (e.g. the customer's manual per-area tech pick survives the
      // wizard's designTemplates re-fire).
      const preserved = existingByArea[group.canonicalName];
      if (preserved) {
        selectedTechByArea[group.canonicalName] = preserved;
        return;
      }
      selectedTechByArea[group.canonicalName] = group.options[0]?.technology ?? '';
    });

    dispatch(setMultiTechAreaGroupsAction({ areaGroups, selectedTechByArea }));
  };

export default initMultiTechAreaGroupsOperation;
